import * as classNames from 'classnames';
import padStart from 'lodash-es/padStart';
import { computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as moment from 'moment';
import * as React from 'react';

// Styles
const s = require('./DatePickerCalendar.css');
const iconBlueArrow = require('assets/blue arrow.svg');

// Interfaces
interface IDateObject {
    id: string;
    date: number;
    dateString: string;
    isSelected: boolean;
    isCurrentMonth: boolean;
    isToday: boolean;
}

interface IMonthObject {
    id: string;
    month: number;
    monthString: string;
    isSelected: boolean;
}

// Props
interface IDatePickerCalendarProps {
    onChange: ((date: moment.Moment | null) => void) | null;
    className?: string | null;
    value: moment.Moment | null;
}

@observer
export class DatePickerCalendar extends React.Component<
    IDatePickerCalendarProps,
    {}
> {
    static get defaultProps(): IDatePickerCalendarProps {
        return {
            onChange: null,
            className: null,
            value: null,
        };
    }

    constructor(props, context) {
        super(props, context);

        this.state = {
            today: moment(),
            showDays: true,
            date: {
                selected: null,
                displayed: null,
            },
            weekDayNames: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        };
    }

    private get now(): moment.Moment {
        return moment();
    }
    private get weekDayNames(): string[] {
        return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    }

    @observable private showDays: boolean = true;

    @computed private get selectedDate(): moment.Moment {
        let date: moment.Moment =
            this.props.value &&
            typeof this.props.value.isValid !== 'undefined' &&
            this.props.value.isValid()
                ? this.props.value.clone()
                : moment(this.props.value);

        if (date.isValid() === false) {
            date = moment();
        }

        return date;
    }

    @observable private displayedDate: moment.Moment = this.selectedDate;

    @computed private get daysToggleLabel(): string {
        return this.displayedDate
            ? this.showDays
                ? this.displayedDate.format('MMMM YYYY')
                : this.displayedDate.format('YYYY')
            : '';
    }

    @computed private get weekDays(): Array<{ key: any; label: string }> {
        return this.showDays
            ? this.weekDayNames.map((dayName, dayId) => ({
                  key: dayName,
                  label: dayName,
              }))
            : [{ key: 'monthsLine', label: 'Jump to any month' }];
    }

    @computed private get calendarWeeksAndDays(): Array<{
        id: string;
        days: IDateObject[];
    }> {
        // Prepare object structure
        const weeks = [];
        let days = [];

        // Start from Sunday - might be in previous month
        const startDate = this.displayedDate.clone().date(1);
        while (startDate.day() > 0) {
            startDate.subtract(1, 'days');
        }

        // Iterate all days in 6 weeks
        let iteratedDate = startDate.clone();
        while (weeks.length < 6) {
            // Create day object
            days.push(this.createDateObject(iteratedDate));

            // If week ends, push all days to the week
            if (iteratedDate.day() >= 6) {
                weeks.push({
                    days,
                    id:
                        iteratedDate.year().toString() +
                        'W' +
                        padStart(iteratedDate.isoWeek().toString(), 2, '0'),
                });
                days = [];
            }

            // Move to next day
            iteratedDate.add(1, 'day');
        }
        iteratedDate = null;

        return weeks;
    }

    @computed private get calendarMonths(): Array<{
        id: string;
        months: IMonthObject[];
    }> {
        // Prepare object structure
        const rows = [];
        let months = [];

        // Start from January
        const startDate = this.displayedDate.clone().month(0);

        // Iterate all months
        let iteratedDate = startDate.clone();
        while (rows.length < 6) {
            // Create month object
            months.push(this.createMonthObject(iteratedDate));

            // If row ends, push all months to the row
            if (months.length >= 2) {
                rows.push({
                    months,
                    id:
                        iteratedDate.year().toString() +
                        'R' +
                        (rows.length + 1).toString(),
                });
                months = [];
            }

            // Move to next day
            iteratedDate.add(1, 'month');
        }
        iteratedDate = null;

        return rows;
    }

    public render() {
        // Render
        return (
            <div className={classNames(s.calendar, this.props.className)}>
                <div className={classNames(s.row, s.navigation)}>
                    <div className={s.col}>
                        <button onClick={this.handlePeriodArrowClick(false)}>
                            <img
                                src={iconBlueArrow}
                                style={{ transform: 'scaleX(-1.0)' }}
                            />
                        </button>
                    </div>

                    <div className={s.col}>
                        {this.daysToggleLabel && (
                            <button
                                onClick={this.handleDaysMonthsToggle}
                                className={s.daysMonthsToggle}
                            >
                                {this.daysToggleLabel}
                            </button>
                        )}
                    </div>

                    <div className={s.col}>
                        <button onClick={this.handlePeriodArrowClick(true)}>
                            <img src={iconBlueArrow} />
                        </button>
                    </div>
                </div>

                <div className={classNames(s.row, s.weekDays)}>
                    {this.weekDays.map(day => (
                        <div className={s.col} key={day.key}>
                            <p>{day.label}</p>
                        </div>
                    ))}
                </div>

                {this.showDays &&
                    this.selectedDate &&
                    this.displayedDate &&
                    this.calendarWeeksAndDays.length > 0 && (
                        <div className={s.days}>
                            {this.calendarWeeksAndDays.map(week => {
                                return (
                                    <div className={s.row} key={week.id}>
                                        {week.days.map(day => {
                                            return (
                                                <div
                                                    key={week.id + ' ' + day.id}
                                                    className={s.col}
                                                >
                                                    <button
                                                        onClick={this.handleDateChange(
                                                            day.id
                                                        )}
                                                        className={classNames({
                                                            [s.daySelected]:
                                                                day.isSelected,
                                                            [s.dayNotCurrentMonth]:
                                                                day.isCurrentMonth ===
                                                                false,
                                                            [s.dayToday]:
                                                                day.isToday,
                                                        })}
                                                    >
                                                        {day.dateString}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                {this.showDays === false &&
                    this.selectedDate &&
                    this.displayedDate &&
                    this.calendarMonths.length > 0 && (
                        <div className={s.months}>
                            {this.calendarMonths.map(row => {
                                return (
                                    <div className={s.row} key={row.id}>
                                        {row.months.map(month => {
                                            return (
                                                <div key={month.id}>
                                                    <button
                                                        onClick={this.handleDisplayedMonthChange(
                                                            month.id
                                                        )}
                                                        className={classNames({
                                                            [s.monthSelected]:
                                                                month.isSelected,
                                                        })}
                                                    >
                                                        {month.monthString}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </div>
                    )}
            </div>
        );
    }

    private onChangeCallback = (date: moment.Moment) => {
        if (this.props.onChange) {
            this.props.onChange(date);
        }
    };

    private handlePeriodArrowClick = (toRight: boolean) => (
        e: React.MouseEvent<HTMLButtonElement>
    ) => {
        // Calculate next date
        const nextDisplayedDate = this.displayedDate.clone();
        nextDisplayedDate.date(15);
        if (toRight) {
            if (this.showDays) {
                nextDisplayedDate.add(1, 'month');
            } else {
                nextDisplayedDate.add(1, 'year');
            }
        } else {
            if (this.showDays) {
                nextDisplayedDate.subtract(1, 'month');
            } else {
                nextDisplayedDate.subtract(1, 'year');
            }
        }

        this.displayedDate = nextDisplayedDate;
    };

    private handleDaysMonthsToggle = () => {
        this.showDays = !this.showDays;
    };

    private handleDateChange = (dateString: string) => (
        e: React.MouseEvent<HTMLButtonElement>
    ) => {
        // Parse date string
        const nextDate = moment(dateString);

        // Update state
        if (nextDate.isValid()) {
            this.displayedDate = nextDate;
            this.onChangeCallback(nextDate);
        }
    };

    private handleDisplayedMonthChange = (dateString: string) => (
        e: React.MouseEvent<HTMLButtonElement>
    ) => {
        // Parse date string
        const nextDisplayedDate = moment(dateString + '-15');

        // Update state
        if (nextDisplayedDate.isValid()) {
            this.displayedDate = nextDisplayedDate;
            this.showDays = true;
        }
    };

    private createDateObject = (date): IDateObject | null => {
        return date && this.selectedDate
            ? {
                  id: date.format('YYYY-MM-DD'),
                  date: date.date(),
                  dateString: date.format('DD'),
                  isSelected: date.isSame(this.selectedDate, 'day'),
                  isCurrentMonth: date.isSame(this.displayedDate, 'month'),
                  isToday: date.isSame(this.now, 'day'),
              }
            : null;
    };

    private createMonthObject = (date): IMonthObject | null => {
        return date && this.selectedDate
            ? {
                  id: date.format('YYYY-MM'),
                  month: date.month(),
                  monthString: date.format('MMMM'),
                  isSelected: date.isSame(this.selectedDate, 'month'),
              }
            : null;
    };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/components/calendar/DatePickerCalendar.tsx